'use client';

import { createContext, useMemo, type PropsWithChildren } from 'react';
import type { Session } from '@supabase/supabase-js';
import { useAuthProvider } from './use-auth-provider.ts';
import type { AuthState, AuthActions } from './types.ts';

// -----------------------------------------------------------------------------
// Contexts
// -----------------------------------------------------------------------------

export const AuthStateContext = createContext<AuthState | null>(null);
export const AuthActionsContext = createContext<AuthActions | null>(null);

// -----------------------------------------------------------------------------
// Provider
// -----------------------------------------------------------------------------

export type AuthProviderProps = PropsWithChildren<{
  initialSession?: Session | null;
}>;

export function AuthProvider({ children, initialSession }: AuthProviderProps) {
  const { state: internalState, actions } = useAuthProvider(initialSession);

  // Derive public AuthState from internal state
  const authState = useMemo<AuthState>(
    () => ({
      session: internalState.session,
      user: internalState.session?.user ?? null,
      isLoading: internalState.isLoading,
      isAuthenticated: !!internalState.session,
      error: internalState.error,
      isAnonymous: internalState.isAnonymous,
    }),
    [internalState]
  );

  return (
    <AuthStateContext.Provider value={authState}>
      <AuthActionsContext.Provider value={actions}>
        {children}
      </AuthActionsContext.Provider>
    </AuthStateContext.Provider>
  );
}
